/**
 * @module @yimoka/i18n/sync/manager
 * @summary 语言包同步管理器
 * @author keep
 * @since 2026-05-19
 */

import type { i18n } from 'i18next';

import type { BundledRegistry } from '../bundled/registry';
import { applyBundleToI18n } from '../remote/apply-bundle';
import { fetchRemoteBundle, type FetchBundleResult, writeMemoryCache } from '../remote/fetch-bundle';
import type {
  FetchRemoteBundle,
  I18nCacheMeta,
  I18nStorage,
  I18nSyncPolicy,
  RemoteSharedState,
  SubscribeAppResume,
  SyncManager,
} from '../types';

import { stableHash } from './hash';
import { wrapStorage, type InternalSnapshotStorage } from './storage';

/** SyncManager 工厂配置 */
export interface CreateSyncManagerOptions {
  registry: BundledRegistry;
  fetchRemote: FetchRemoteBundle;
  storage?: I18nStorage;
  policy: I18nSyncPolicy;
  supportedLngs: string[];
  state: RemoteSharedState;
  onLoadingChange?: (loading: boolean) => void;
  subscribeAppResume?: SubscribeAppResume;
}

interface FreshBundle {
  bundle: Record<string, unknown>;
  hash: string;
}

const resolveFreshBundle = (
  result: FetchBundleResult,
  cachedMeta: I18nCacheMeta | undefined,
): FreshBundle | null => {
  if (result.failed || result.notModified || !result.bundle) {
    return null;
  }

  const hash = stableHash(result.bundle);
  if (cachedMeta?.hash && cachedMeta.hash === hash) {
    return null;
  }

  return { bundle: result.bundle, hash };
};

/**
 * 创建同步管理器。
 *
 * @param options - 配置
 * @returns SyncManager
 */
export function createSyncManager(options: CreateSyncManagerOptions): SyncManager {
  const {
    registry,
    fetchRemote,
    storage,
    policy,
    supportedLngs,
    state,
    onLoadingChange,
    subscribeAppResume,
  } = options;

  const snapshot: InternalSnapshotStorage | null = storage ? wrapStorage(storage) : null;

  let appResumeUnsub: (() => void) | undefined;
  let lastResumeSyncAt = 0;
  let started = false;

  async function getCachedMeta(lang: string): Promise<I18nCacheMeta | undefined> {
    if (!snapshot) {
      return undefined;
    }
    return (await snapshot.getSnapshot(lang))?.meta;
  }

  async function persistFreshBundle(
    i18nInstance: i18n,
    lang: string,
    fresh: FreshBundle,
    cachedMeta: I18nCacheMeta | undefined,
  ): Promise<void> {
    applyBundleToI18n(i18nInstance, lang, fresh.bundle, registry);
    writeMemoryCache(state, lang, fresh.bundle);
    await snapshot?.setSnapshot(lang, fresh.bundle, { hash: fresh.hash, etag: cachedMeta?.etag });
  }

  /**
   * 从 storage 加载快照并注入 i18n。
   *
   * @param i18nInstance - i18next 实例
   */
  async function loadStorageSnapshots(i18nInstance: i18n): Promise<void> {
    if (!snapshot || policy.onStartup === 'bundled_only') {
      return;
    }

    await Promise.all(
      supportedLngs.map(async (lang) => {
        const saved = await snapshot.getSnapshot(lang);
        if (saved?.data) {
          applyBundleToI18n(i18nInstance, lang, saved.data, registry);
          writeMemoryCache(state, lang, saved.data);
        }
      }),
    );
  }

  /**
   * 刷新某语种：hash 变化才更新 i18n 与 storage。
   *
   * @param i18nInstance - i18next 实例
   * @param lang - 语种短码
   */
  async function refreshLang(i18nInstance: i18n, lang: string): Promise<void> {
    const cachedMeta = await getCachedMeta(lang);

    onLoadingChange?.(true);
    try {
      const result = await fetchRemoteBundle(
        fetchRemote,
        lang,
        { i18nextLng: lang, cachedMeta },
        state,
      );

      const fresh = resolveFreshBundle(result, cachedMeta);
      if (!fresh) {
        return;
      }

      await persistFreshBundle(i18nInstance, lang, fresh, cachedMeta);
    } finally {
      onLoadingChange?.(false);
    }
  }

  async function backgroundRefreshAll(i18nInstance: i18n): Promise<void> {
    if (!policy.backgroundRefresh) {
      return;
    }
    await Promise.all(supportedLngs.map(lang => refreshLang(i18nInstance, lang)));
  }

  function handleAppResumeSync(i18nInstance: i18n): void {
    const now = Date.now();
    if (now - lastResumeSyncAt < policy.resumeSyncIntervalMs) {
      return;
    }
    lastResumeSyncAt = now;
    void backgroundRefreshAll(i18nInstance);
  }

  return {
    async start(i18nInstance: i18n): Promise<void> {
      if (started) {
        return;
      }
      started = true;

      await loadStorageSnapshots(i18nInstance);

      if (policy.backgroundRefresh) {
        void backgroundRefreshAll(i18nInstance);
      }

      if (subscribeAppResume && policy.resumeSyncIntervalMs > 0) {
        appResumeUnsub = subscribeAppResume(() => {
          handleAppResumeSync(i18nInstance);
        });
      }
    },

    stop(): void {
      appResumeUnsub?.();
      appResumeUnsub = undefined;
      started = false;
    },

    refreshLang,
  };
}
